import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    // Capture size changes before the variant picker dispatches its product-select event.
    target?.addEventListener('change', this.#handleSizeOptionChange, { capture: true, signal });
    target?.addEventListener('click', this.#handleChristmasSizeClick, { capture: true, signal });
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('click', this.#handleSizeGuideAction, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #showSizeGuideOnNextSelection = false;

  // Shopify's size options may be displayed as radio buttons or dropdowns.
  #handleSizeOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    if (!event.target.closest('variant-picker') || event.target.closest('product-card, quick-add-dialog')) return;

    const input = event.target;
    const option = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
    const optionName = option?.dataset.optionName?.trim() || '';
    if (/^(size|taglia|taille|tama\u00f1o|gr\u00f6\u00dfe|grosse|dimensioni|dimensions|maat|tamanho)$/i.test(optionName)) {
      this.#showSizeGuideOnNextSelection = true;
    }
  };

  // For the Christmas custom-pet pouch, reselecting an already active size
  // should still display the size chart even without a variant change event.
  #handleChristmasSizeClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label.variant-option__button-label');
    const picker = label?.closest('variant-picker[data-product-id="11350876619095"]');
    const input = label?.querySelector('input[type="radio"][data-option-name]');
    if (!picker || !input) return;

    if (!/^(size|taglia|taille|tamaño|größe|grosse|dimensioni|dimensions|maat|tamanho)$/i.test(input.dataset.optionName || '')) return;

    if (input.checked) {
      void this.#focusSizeGuide(this);
    } else {
      this.#showSizeGuideOnNextSelection = true;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Reads a size from buttons or a dropdown; used only to verify what changed.
   * @param {Element | null} picker
   * @returns {string}
   */
  #readPickerSize(picker) {
    if (!picker) return '';
    const controls = picker.querySelectorAll('fieldset input:checked, select');
    for (const control of controls) {
      const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
      const name = option?.dataset?.optionName?.trim() || '';
      if (/^(size|taglia|taille|tamaño|größe|grosse|dimensioni|dimensions|maat|tamanho)$/i.test(name)) {
        return option.value?.trim() || '';
      }
    }
    return '';
  }

  /**
   * Waits until the replacement carousel is initialized before selecting the
   * already-uploaded size guide. Stops if the carousel is replaced again.
   * @param {MediaGallery} gallery
   */
  async #focusSizeGuide(gallery) {
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // The slideshow's scroll controller is initialized asynchronously.
    for (let attempt = 0; attempt < 12; attempt++) {
      if (!gallery.isConnected) return;
      const slideshow = gallery.slideshow;
      if (slideshow) {
        const sizeGuideSlide = Array.from(slideshow.querySelectorAll('slideshow-slide')).find((slide) => {
          const image = slide.querySelector('img.product-media__image');
          if (!image) return false;
          const altAndUrl = (image.getAttribute('alt') || '') + ' ' + (image.getAttribute('src') || '');
          return /size[\s_-]*(?:guide|chart)|guida alle taglie|guide des tailles|guía de tallas/i.test(altAndUrl);
        });

        // Products without a size guide keep their normal variant-image behavior.
        if (!sizeGuideSlide) return;

        const slides = slideshow.slides || [];
        const index = slides.indexOf(sizeGuideSlide);
        if (index !== -1) {
          await slideshow.select(index, undefined, { animate: false });
          if (slideshow.current === index) return;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 75));
    }
  }

  #handleSizeGuideAction = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-sv-size-guide-trigger]');
    if (!trigger || trigger.closest('product-card, quick-add-dialog')) return;
    event.preventDefault();
    void this.#focusSizeGuide(this);
  };

  /**
   * Select the size-guide image deterministically before the refreshed gallery
   * is connected: the Slideshow reads initial-slide during connectedCallback.
   * Give the target slide an id so Slideshow's initial scroll logic can resolve it.
   * @param {MediaGallery} gallery
   * @returns {boolean}
   */
  #setSizeGuideInitialSlide(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow) return false;
    const slides = Array.from(slideshow.querySelectorAll('slideshow-slides > slideshow-slide'));
    const index = slides.findIndex((slide) => {
      const image = slide.querySelector('img.product-media__image');
      if (!image) return false;
      const label = (image.getAttribute('alt') || '') + ' ' + (image.getAttribute('src') || '');
      return /size[\s_-]*(?:guide|chart)|guida alle taglie|guide des tailles|guía de tallas/i.test(label);
    });
    if (index < 0) return false;
    const target = slides[index];
    target.setAttribute('slide-id', target.getAttribute('slide-id') || 'sv-size-guide-slide');
    slideshow.setAttribute('initial-slide', String(index));
    return true;
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // A size selection also updates the variant picker before the product-select event.
    // Compare the selected size with the size of the currently rendered gallery as a fallback
    // when the native 'change' event is missed by the carousel.
    const selectedPicker = event.target.closest('variant-picker');
    const nextSize = this.#readPickerSize(selectedPicker);
    const oldSize = this.dataset.svSelectedSize || '';
    const showSizeGuide = this.#showSizeGuideOnNextSelection ||
      Boolean(oldSize && nextSize && oldSize !== nextSize);
    this.#showSizeGuideOnNextSelection = false;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (showSizeGuide) {
          this.#setSizeGuideInitialSlide(newMediaGallery);
        }
        this.replaceWith(newMediaGallery);

        if (showSizeGuide) {
          // Secondary fallback if slideshow was already initialized before
          // the replacement. The initial-slide attribute handles first paint.
          void this.#focusSizeGuide(newMediaGallery);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
